import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    IconButton,
    Avatar,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Menu,
    MenuItem,
    Tooltip,
    Switch,
    FormControlLabel,
    Divider,
    CircularProgress,
} from '@mui/material';
import {
    IconMapPin,
    IconPhone,
    IconBrandWhatsapp,
    IconDotsVertical,
    IconHeart,
    IconTruck,
    IconBolt,
    IconEdit,
    IconTrash,
    IconCircleCheck,
    IconCircleX,
} from '@tabler/icons-react';
import { colors, fonts } from '../ColorFont/color';
import { url } from '../ConstUrl/Url';
import Carousel from 'react-material-ui-carousel';
import axios from 'axios';
import { toast } from 'react-toastify';
import BoostListingDialog from './BoostListingDialog';
import { useTranslation } from 'react-i18next';

const UnifiedCropCard = ({ 
    crop, 
    userData, 
    onGetContact, 
    onBoost, 
    onDetails,
    onDeleteSuccess,
    onEdit,
    isExplore,
    isAdmin,
    onToggleEnable
}) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    
    // Check if wishlisted from userData
    const initialFavorite = userData?.wishlist?.some(id => 
        (typeof id === 'string' ? id : id._id) === crop._id
    ) || false;

    const [isFavorite, setIsFavorite] = useState(initialFavorite);
    const [isWishlisting, setIsWishlisting] = useState(false);

    // Keep isFavorite in sync with userData prop
    React.useEffect(() => {
        setIsFavorite(initialFavorite);
    }, [initialFavorite]);

    const [showFarmerPrompt, setShowFarmerPrompt] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [boostDialogOpen, setBoostDialogOpen] = useState(false);
    const [isAvailable, setIsAvailable] = useState(crop.isAvailable !== false); // default to true
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

    const openMenu = Boolean(anchorEl);

    if (!crop) return null;

    const isOwner = isAdmin || userData?._id === crop.user || userData?._id === crop.user?._id;
    const isFarmer = userData?.role === 'Seller' || userData?.role === 'Farmer';
    const isSupplier = userData?.role === 'Buyer' || userData?.role === 'Supplier';
    const isBoosted = crop.sponsoredHoursRemaining > 0;
    
    // Check if supplier has purchased this plot
    const hasPurchased = userData?.purchasedPlots?.some(
        (p) => (p.plotId?._id || p.plotId) === crop._id
    );

    const photos = Array.isArray(crop.photos) ? crop.photos : [];
    const firstImage = photos.length > 0 
        ? `${url}/${photos[0]}` 
        : 'https://placehold.co/600x400/E8F5E9/16A249?text=No+Image';

    const handleMenuOpen = (e) => {
        e.stopPropagation();
        setAnchorEl(e.currentTarget);
    };

    const handleStatusToggle = async () => {
        try {
            setIsUpdatingStatus(true);
            const response = await axios.post(`${url}/api/update-plot`, {
                plotId: crop._id,
                isAvailable: !isAvailable,
                user: userData?._id
            });

            if (response.status === 200) {
                setIsAvailable(!isAvailable);
                toast.success(t('statusUpdatedSuccess') || 'Status updated successfully');
                // Trigger an event to refresh data in parent components if needed
                window.dispatchEvent(new CustomEvent('plotStatusUpdated', { detail: { plotId: crop._id, isAvailable: !isAvailable } }));
            }
        } catch (error) {
            console.error("Error updating plot status:", error);
            toast.error(t('statusUpdateError') || "Error updating status");
        } finally {
            setIsUpdatingStatus(false);
            setShowStatusModal(false);
        }
    };

    const handleMenuClose = (e) => {
        if (e) e.stopPropagation();
        setAnchorEl(null);
    };

    const handleEditClick = (e) => {
        e.stopPropagation();
        handleMenuClose();
        if (onEdit) onEdit(crop);
    };

    const handleDeleteClick = (e) => {
        e.stopPropagation();
        handleMenuClose();
        setShowDeleteModal(true);
    };

    const confirmDelete = async (e) => {
        e.stopPropagation();
        setIsDeleting(true);
        try {
            const response = await axios.delete(`${url}/api/plots/${crop._id}`);
            if (response.status === 200) {
                toast.success("Listing deleted successfully");
                if (onDeleteSuccess) onDeleteSuccess(crop._id);
                setShowDeleteModal(false);
            }
        } catch (error) {
            console.error("Delete error:", error);
            toast.error(error.response?.data?.message || "Failed to delete listing");
        } finally {
            setIsDeleting(false);
        }
    };

    const handleGetContactClick = (e) => {
        e.stopPropagation();
        if (isFarmer && !isOwner) {
            setShowFarmerPrompt(true);
        } else {
            onGetContact(crop);
        }
    };

    const handleWishlistToggle = async (e) => {
        e.stopPropagation();
        if (!userData?._id) {
            toast.info("Please login to add to wishlist");
            return;
        }

        setIsWishlisting(true);
        const action = isFavorite ? 'remove' : 'add';
        
        try {
            const response = await axios.post(`${url}/api/wishlist`, {
                userId: userData._id,
                plotId: crop._id,
                action: action
            });

            if (response.status === 200) {
                setIsFavorite(!isFavorite);
                toast.success(action === 'add' ? "Added to wishlist" : "Removed from wishlist", {
                    position: "bottom-right",
                    autoClose: 2000
                });
                
                // Update local storage to keep sync without reload
                const updatedUser = { ...userData };
                if (action === 'add') {
                    updatedUser.wishlist = [...(updatedUser.wishlist || []), crop._id];
                } else {
                    updatedUser.wishlist = (updatedUser.wishlist || []).filter(id => 
                        (typeof id === 'string' ? id : id._id) !== crop._id
                    );
                }
                localStorage.setItem('userData', JSON.stringify(updatedUser));
                
                // Dispatch event to sync across app
                window.dispatchEvent(new Event('userDataUpdated'));
            }
        } catch (error) {
            console.error("Wishlist error:", error);
            toast.error("Failed to update wishlist");
        } finally {
            setIsWishlisting(false);
        }
    };

    return (
        <Box
            onClick={() => navigate(`/crop/${crop._id}`)}
            sx={{
                width: '100%',
                backgroundColor: '#FFFFFF',
                borderRadius: '24px',
                border: '1px solid #E0E5EB',
                overflow: 'hidden',
                boxShadow: '0px 2px 12px rgba(0,0,0,0.04)',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                '&:hover': {
                    boxShadow: '0px 8px 24px rgba(0,0,0,0.12)',
                    transform: 'translateY(-4px)',
                },
            }}
        >
            {/* Status Change Dialog */}
            <Dialog 
                open={showStatusModal} 
                onClose={() => !isUpdatingStatus && setShowStatusModal(false)}
                onClick={(e) => e.stopPropagation()}
            >
                <DialogTitle sx={{ fontFamily: fonts.poppins, fontWeight: 700 }}>
                    {isAvailable ? "Confirm Sold Out" : "Confirm Available"}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ fontFamily: fonts.publicSans }}>
                        {isAvailable 
                            ? "Are you sure you want to mark this crop as sold out? It will still be visible but marked as unavailable." 
                            : "Are you sure you want to mark this crop as available for sale?"}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button 
                        onClick={() => setShowStatusModal(false)} 
                        disabled={isUpdatingStatus}
                        sx={{ fontFamily: fonts.poppins, fontWeight: 600, color: '#637381' }}
                    >
                        CANCEL
                    </Button>
                    <Button 
                        onClick={handleStatusToggle} 
                        variant="contained"
                        disabled={isUpdatingStatus}
                        sx={{ 
                            bgcolor: isAvailable ? '#FF4842' : '#00AB55',
                            fontFamily: fonts.poppins, 
                            fontWeight: 700,
                            borderRadius: '8px',
                            '&:hover': { bgcolor: isAvailable ? '#B71D18' : '#007B55' }
                        }}
                    >
                        {isUpdatingStatus ? <CircularProgress size={24} color="inherit" /> : (isAvailable ? 'Mark Sold Out' : 'Mark Available')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Image Section */}
            <Box sx={{ position: 'relative', height: '220px', overflow: 'hidden' }}>
                <Carousel
                    autoPlay={false}
                    animation="slide"
                    indicators={photos.length > 1}
                    navButtonsAlwaysVisible={photos.length > 1}
                    sx={{ height: '100%' }}
                >
                    {photos.length > 0 ? (
                        photos.map((photo, index) => (
                            <Box
                                key={index}
                                component="img"
                                src={`${url}/${photo}`}
                                sx={{
                                    width: '100%',
                                    height: '220px',
                                    objectFit: 'cover',
                                    transition: 'transform 0.5s ease',
                                    '&:hover': { transform: 'scale(1.05)' }
                                }}
                            />
                        ))
                    ) : (
                        <Box
                            component="img"
                            src="https://placehold.co/600x400/E8F5E9/16A249?text=No+Image"
                            sx={{ width: '100%', height: '220px', objectFit: 'cover' }}
                        />
                    )}
                </Carousel>

                {!isAvailable && (
                    <Box sx={{
                        position: 'absolute',
                        top: 0, left: 0, right: 0, bottom: 0,
                        bgcolor: 'rgba(0,0,0,0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 2,
                        pointerEvents: 'none'
                    }}>
                        <Typography sx={{
                            color: '#FFFFFF',
                            fontFamily: fonts.poppins,
                            fontWeight: 900,
                            fontSize: '24px',
                            px: 3, py: 1,
                            border: '4px solid #FFFFFF',
                            borderRadius: '8px',
                            transform: 'rotate(-15deg)',
                            textTransform: 'uppercase',
                            letterSpacing: '2px'
                        }}>
                            {t('soldOut') || 'SOLD OUT'}
                        </Typography>
                    </Box>
                )}

                {/* Badges */}
                {/* Premium Sponsor Tag - Top Left */}
                <Box sx={{ 
                    position: 'absolute', 
                    top: 12, 
                    left: 12, 
                    zIndex: 20 
                }}>
                    {isBoosted && (
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '6px',
                            bgcolor: 'rgba(255, 255, 255, 0.9)', 
                            color: '#1C252E',
                            padding: '4px 10px',
                            borderRadius: '8px',
                            boxShadow: '0px 2px 8px rgba(0,0,0,0.1)',
                            border: '1px solid rgba(0,0,0,0.05)',
                            backdropFilter: 'blur(4px)',
                            animation: 'pulse-badge 2s infinite ease-in-out'
                        }}>
                            <IconBolt size={14} fill="#F59E0B" color="#F59E0B" />
                            <Typography sx={{ 
                                fontSize: '10px', 
                                fontWeight: 800, 
                                fontFamily: fonts.poppins,
                                letterSpacing: '0.5px',
                                textTransform: 'uppercase'
                            }}>
                                {t('sponsoredBadge')}
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* Badges - Right Side Overlay */}
                <Box sx={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    gap: 1,
                    zIndex: 20
                }}>
                    <IconButton
                        disabled={isWishlisting}
                        onClick={handleWishlistToggle}
                        sx={{
                            bgcolor: 'rgba(255,255,255,0.85)',
                            backdropFilter: 'blur(4px)',
                            padding: '10px',
                            '&:hover': { bgcolor: '#FFFFFF', transform: 'scale(1.1)' },
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            boxShadow: '0px 2px 8px rgba(0,0,0,0.1)'
                        }}
                    >
                        <IconHeart 
                            size={22} 
                            fill={isFavorite ? colors.primary : 'none'} 
                            color={isFavorite ? colors.primary : '#637381'} 
                        />
                    </IconButton>
                </Box>

                {/* Negotiable Badge - Bottom Left */}
                {crop.isNegotiable && (
                    <Box sx={{
                        position: 'absolute',
                        bottom: 12,
                        left: 12,
                        zIndex: 20
                    }}>
                        <Typography sx={{ 
                            bgcolor: 'rgba(255, 255, 255, 0.9)', 
                            px: 1.5, py: 0.5, borderRadius: '8px',
                            fontSize: '11px', fontWeight: 700, color: '#1C252E',
                            fontFamily: fonts.poppins,
                            backdropFilter: 'blur(4px)',
                            border: '1px solid rgba(0,0,0,0.05)',
                            boxShadow: '0px 4px 10px rgba(0,0,0,0.05)'
                        }}>
                            {t('negotiableBadge')}
                        </Typography>
                    </Box>
                )}

                {/* Grade Badge - Bottom Right */}
                <Box sx={{
                    position: 'absolute',
                    bottom: 12,
                    right: 12,
                    zIndex: 20
                }}>
                    <Typography sx={{ 
                        bgcolor: 'rgba(255, 255, 255, 0.9)', 
                        px: 1.5, py: 0.5, borderRadius: '8px',
                        fontSize: '11px', fontWeight: 700, color: '#1C252E',
                        fontFamily: fonts.poppins,
                        backdropFilter: 'blur(4px)',
                        border: '1px solid rgba(0,0,0,0.05)',
                        boxShadow: '0px 4px 10px rgba(0,0,0,0.05)'
                    }}>
                        {crop.grade && crop.grade.toLowerCase().includes('grade') 
                            ? crop.grade 
                            : `${t('gradeBadge') || 'Grade'} ${crop.grade || 'A'}`}
                    </Typography>
                </Box>
            </Box>

            {/* Content Section */}
            <Box sx={{ p: 2, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                    <Box>
                        <Typography sx={{ 
                            fontFamily: fonts.poppins, 
                            fontWeight: 700, 
                            fontSize: '16px', 
                            color: '#1C252E',
                            lineHeight: 1.2
                        }}>
                            {t(`crops.${(crop.subCategory || crop.name || '').replace('crops.', '')}`, { defaultValue: (crop.subCategory || crop.name || '').replace('crops.', '') })}{crop.variety ? ` - ${crop.variety}` : ''}
                        </Typography>
                        <Typography sx={{ 
                            fontFamily: fonts.poppins, 
                            fontSize: '13px', 
                            color: '#637381',
                            mt: 0.2
                        }}>
                            {t(`categories.${Array.isArray(crop.category) ? crop.category[0] : (crop.category || 'Produce')}`, { defaultValue: Array.isArray(crop.category) ? crop.category[0] : (crop.category || 'Produce') })}
                        </Typography>
                    </Box>
                    {isOwner && (
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip title={isAvailable ? "Mark Sold Out" : "Mark Available"} arrow placement="bottom">
                                <IconButton 
                                    size="small" 
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setShowStatusModal(true);
                                    }}
                                    sx={{ 
                                        color: isAvailable ? '#FF4842' : '#00AB55',
                                        '&:hover': { bgcolor: isAvailable ? '#FF484210' : '#00AB5510' } 
                                     }}
                                >
                                    {isAvailable ? <IconCircleX size={20} stroke={2} /> : <IconCircleCheck size={20} stroke={2} />}
                                </IconButton>
                            </Tooltip>

                            <Tooltip title="Edit Listing" arrow placement="bottom">
                                <IconButton 
                                    size="small" 
                                    onClick={handleEditClick}
                                    sx={{ 
                                        color: '#919EAB',
                                        '&:hover': { bgcolor: '#F4F6F8', color: colors.primary } 
                                     }}
                                >
                                    <IconEdit size={20} stroke={2} />
                                </IconButton>
                            </Tooltip>
                            
                            <Tooltip title="Delete Listing" arrow placement="bottom">
                                <IconButton 
                                    size="small" 
                                    onClick={handleDeleteClick}
                                    sx={{ 
                                        color: '#919EAB',
                                        '&:hover': { bgcolor: '#FF563008', color: '#FF5630' } 
                                    }}
                                >
                                    <IconTrash size={20} stroke={2} />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    )}
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1.5 }}>
                    <Typography sx={{ 
                        fontFamily: fonts.poppins, 
                        fontWeight: 700, 
                        fontSize: '18px', 
                        color: 'var(--primary-color)' 
                    }}>
                        ₹{crop.amount}<span style={{ fontSize: '13px', fontWeight: 400, color: '#637381' }}>/{crop.priceUnit || crop.unit || 'Kg'}</span>
                    </Typography>
                    <Typography sx={{ 
                        fontFamily: fonts.poppins, 
                        fontSize: '14px', 
                        fontWeight: 600, 
                        color: '#1C252E' 
                    }}>
                        {crop.area} {crop.areaUnit || 'Quintal'}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1.5, mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <IconMapPin size={16} color="#919EAB" />
                        <Typography sx={{ 
                            fontSize: '13px', 
                            color: '#637381',
                            display: '-webkit-box',
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                        }}>
                            {crop.taluka || crop.district || crop.village || t('locationFallback')}, {crop.state}
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* Spacer to push buttons down */}
            <Box sx={{ flex: 1 }} />

            {/* Bottom Section */}
            <Box sx={{ p: 2, pt: 0 }}>
                <Box sx={{ my: 1, height: '1px', bgcolor: '#F4F6F8' }} />

                <Box sx={{ height: '44px', display: 'flex', alignItems: 'center', mb: 1 }}>
                    {!isOwner ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar 
                                src={
                                    crop.user?.profilePhoto
                                        ? (crop.user?.profilePhoto.startsWith('http') 
                                            ? crop.user?.profilePhoto 
                                            : `${url}/${crop.user?.profilePhoto}`)
                                        : undefined
                                } 
                                sx={{ width: 32, height: 32, bgcolor: colors.primary_light, fontSize: '14px', fontWeight: 700 }}
                            >
                                {!crop.user?.profilePhoto 
                                    ? (crop.user?.firstName || crop.user?.name || 'F').charAt(0).toUpperCase() 
                                    : null}
                            </Avatar>
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                <Typography sx={{ fontFamily: fonts.poppins, fontSize: '14px', fontWeight: 600, color: '#1C252E', lineHeight: 1.2 }}>
                                    {(crop.user?.firstName ? `${crop.user.firstName} ${crop.user.lastName || ''}`.trim() : (crop.user?.fullName || crop.user?.name || t('farmerRole')))}
                                </Typography>
                                {crop.user && (
                                    <Typography sx={{ fontSize: '11px', color: '#919EAB', fontFamily: fonts.poppins, mt: 0.2 }}>
                                        {crop.user.tahsil || crop.user.district || crop.user.village || ''}
                                        {(crop.user.tahsil || crop.user.district || crop.user.village) && crop.user.state ? ', ' : ''}
                                        {crop.user.state || ''}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ 
                                width: 32, height: 32, borderRadius: '50%', 
                                bgcolor: colors.primary_light, 
                                display: 'flex', alignItems: 'center', justifyContent: 'center' 
                            }}>
                                <IconEdit size={16} color={colors.primary} />
                            </Box>
                            <Typography sx={{ fontFamily: fonts.poppins, fontSize: '13px', fontWeight: 600, color: '#1C252E' }}>
                                {t('yourListing') === 'yourListing' ? 'Your Listing' : t('yourListing')}
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* Role-Based Buttons */}
                <Box sx={{ minHeight: '56px', display: 'flex', alignItems: 'stretch' }}>
                <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                    {(isFarmer || userData?.role === 'farmer') && isOwner ? (
                        <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                            <Button
                                fullWidth
                                variant="contained"
                                disabled={isBoosted}
                                onClick={(e) => { 
                                    e.stopPropagation(); 
                                    setBoostDialogOpen(true); 
                                }}
                                startIcon={<IconBolt size={18} fill={isBoosted ? "none" : "#FFFFFF"} />}
                                sx={{
                                    height: '48px',
                                    borderRadius: '12px',
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    fontSize: '14px',
                                    bgcolor: isBoosted ? '#F4F6F8 !important' : 'var(--primary-color)',
                                    color: isBoosted ? '#919EAB !important' : '#FFFFFF',
                                    boxShadow: 'none',
                                    '&:hover': { 
                                        bgcolor: 'var(--primary_dark_color)',
                                        boxShadow: `0px 4px 12px ${colors.primary_rgba_24}`
                                    }
                                }}
                            >
                                {isBoosted ? t('sponsoredBadge') : t('boostListingBtn')}
                            </Button>
                        </Box>
                    ) : isSupplier && hasPurchased ? (
                        <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                            <Button
                                fullWidth
                                variant="contained"
                                startIcon={<IconPhone size={18} />}
                                onClick={(e) => { e.stopPropagation(); window.location.href = `tel:${crop.contactNumber}`; }}
                                sx={{
                                    height: '48px',
                                    borderRadius: '12px',
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    fontSize: '13px',
                                    bgcolor: colors.primary,
                                    flex: 1,
                                    '&:hover': { bgcolor: colors.primary_dark }
                                }}
                            >
                                {t('callBtn')}
                            </Button>
                            <Button
                                fullWidth
                                variant="contained"
                                startIcon={<IconBrandWhatsapp size={18} />}
                                onClick={(e) => { e.stopPropagation(); window.open(`https://wa.me/${crop.contactNumber}`, '_blank'); }}
                                sx={{
                                    height: '48px',
                                    borderRadius: '12px',
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    fontSize: '13px',
                                    bgcolor: '#25D366',
                                    flex: 1,
                                    '&:hover': { bgcolor: '#128C7E' }
                                }}
                            >
                                {t('whatsappBtn')}
                            </Button>
                        </Box>
                    ) : isExplore ? null : (
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={handleGetContactClick}
                            sx={{
                                height: '48px',
                                borderRadius: '12px',
                                textTransform: 'none',
                                fontWeight: 700,
                                fontSize: '14px',
                                bgcolor: 'var(--primary-color)',
                                '&:hover': { bgcolor: 'var(--primary-dark-color)' }
                            }}
                        >
                            {t('getContactBtn')}
                        </Button>
                    )}
                </Box>
                </Box>

                {isAdmin && (
                    <Box sx={{ mt: 2, pt: 1.5, borderTop: `1px solid #F4F6F8`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                         <FormControlLabel
                            control={
                                <Switch
                                    checked={!crop.isDisabled}
                                    onChange={(e) => onToggleEnable && onToggleEnable(crop._id, !e.target.checked)}
                                    color="primary"
                                    size="small"
                                />
                            }
                            label={
                                <Typography sx={{ fontFamily: fonts.poppins, fontSize: '13px', fontWeight: 600, color: crop.isDisabled ? '#FF5630' : colors.primary }}>
                                    {crop.isDisabled ? t('Disabled') : t('Enabled')}
                                </Typography>
                            }
                        />
                    </Box>
                )}
            </Box>

            <Dialog 
                open={showFarmerPrompt} 
                onClose={() => setShowFarmerPrompt(false)}
                onClick={(e) => e.stopPropagation()}
                PaperProps={{ sx: { borderRadius: '24px', p: 1 } }}
            >
                <DialogContent sx={{ textAlign: 'center', py: 4 }}>
                    <Typography sx={{ 
                        fontFamily: fonts.poppins, 
                        fontWeight: 600, 
                        fontSize: '16px',
                        color: '#1C252E',
                        mb: 2
                    }}>
                        {t('farmerPromptDesc')}
                    </Typography>
                    <Button 
                        onClick={(e) => {
                            e.stopPropagation();
                            setShowFarmerPrompt(false);
                        }}
                        fullWidth
                        sx={{ 
                            mt: 2, 
                            height: '48px', 
                            borderRadius: '12px', 
                            bgcolor: colors.primary,
                            color: '#FFFFFF',
                            fontWeight: 700,
                            fontFamily: fonts.poppins,
                            '&:hover': { bgcolor: colors.primary_dark }
                        }}
                    >
                        {t('gotItBtn')}
                    </Button>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Modal */}
            <Dialog
                open={showDeleteModal}
                onClose={() => !isDeleting && setShowDeleteModal(false)}
                onClick={(e) => e.stopPropagation()}
                PaperProps={{
                    sx: { borderRadius: '20px', padding: '10px', maxWidth: '400px' }
                }}
            >
                <DialogTitle sx={{ fontFamily: fonts.manrope, fontWeight: 800, textAlign: 'center' }}>
                    {t('deleteListingModalTitle')}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ textAlign: 'center', fontFamily: fonts.publicSans }}>
                        {t('deleteListingModalDesc')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 3 }}>
                    <Button 
                        onClick={() => setShowDeleteModal(false)}
                        disabled={isDeleting}
                        sx={{ color: colors.text, fontWeight: 700, minWidth: '100px' }}
                    >
                        {t('cancelBtn')}
                    </Button>
                    <Button 
                        onClick={confirmDelete}
                        disabled={isDeleting}
                        variant="contained"
                        sx={{ 
                            bgcolor: '#FF4D4D', 
                            '&:hover': { bgcolor: '#D32F2F' },
                            fontWeight: 700,
                            borderRadius: '10px',
                            minWidth: '120px'
                        }}
                    >
                        {isDeleting ? t('deletingBtn') : t('deleteNowBtn')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Actions Menu */}
            <Menu
                anchorEl={anchorEl}
                open={openMenu}
                onClose={handleMenuClose}
                onClick={(e) => e.stopPropagation()}
                PaperProps={{
                    sx: {
                        borderRadius: '12px',
                        minWidth: '160px',
                        boxShadow: '0px 4px 20px rgba(0,0,0,0.1)',
                        mt: 0.5
                    }
                }}
            >
                <MenuItem onClick={handleEditClick} sx={{ py: 1.5, gap: 1.5 }}>
                    <IconEdit size={18} color="#637381" />
                    <Typography sx={{ fontFamily: fonts.poppins, fontSize: '14px', fontWeight: 600 }}>
                        {t('edit') || 'Edit'}
                    </Typography>
                </MenuItem>
                
                {isFarmer && (
                    <MenuItem 
                        onClick={(e) => {
                            e.stopPropagation();
                            handleMenuClose();
                            setShowStatusModal(true);
                        }} 
                        sx={{ py: 1.5, gap: 1.5 }}
                    >
                        {isAvailable ? <IconCircleX size={18} color="#FF4842" /> : <IconCircleCheck size={18} color="#00AB55" />}
                        <Typography sx={{ 
                            fontFamily: fonts.poppins, 
                            fontSize: '14px', 
                            fontWeight: 600,
                        }}>
                             {isAvailable ? "Mark Sold Out" : "Mark Available"}
                        </Typography>
                    </MenuItem>
                )}

                <Divider sx={{ my: 0.5, borderStyle: 'dashed' }} />
                
                <MenuItem onClick={handleDeleteClick} sx={{ py: 1.5, gap: 1.5, color: '#FF4842' }}>
                    <IconTrash size={18} />
                    <Typography sx={{ fontFamily: fonts.poppins, fontSize: '14px', fontWeight: 600 }}>
                        {t('deleteBtn') || 'Delete'}
                    </Typography>
                </MenuItem>
            </Menu>

            {/* Boost Scheduler Dialog */}
            <Box onClick={(e) => e.stopPropagation()}>
                <BoostListingDialog 
                    open={boostDialogOpen}
                    onClose={() => setBoostDialogOpen(false)}
                    crop={crop}
                    userData={userData}
                    onUpdateUser={(updatedData) => {
                        // Sync internal storage
                        localStorage.setItem('userData', JSON.stringify(updatedData));
                        
                        // Notify parent to refresh data (e.g., Sponsored badge)
                        if (onBoost) onBoost(updatedData);
                    }}
                />
            </Box>
        </Box>
    );
};

export default UnifiedCropCard;
